/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                            SCRAPER LOGGING SERVICE                                           ║
 * ║                     Enhanced logging for external scraper infrastructure                     ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import { supabase } from '../lib/supabase';
import { EventCode } from './logging-service';

// ============================================================================
// INTERFACES & TYPES
// ============================================================================

export interface ScraperLogEvent {
  scraper_id: string;
  event_code: number;
  message?: string;
  metadata?: Record<string, any>;
  raw_content?: string;
  correlation_id?: string;
  session_id?: string;
  deployment_id?: string;
  source_url?: string;
  performance_metrics?: {
    runtime_ms: number;
    memory_usage_mb: number;
    cpu_usage_percent: number;
    companies_parsed?: number;
  };
}

export interface AIAnalysisRequest {
  scraper_id: string;
  raw_content: string;
  content_type: 'html' | 'json' | 'xml' | 'text';
  source_url?: string;
  context_metadata?: Record<string, any>;
  priority?: 1 | 2 | 3 | 4 | 5; // 1=critical, 5=low
}

export interface DeploymentEvent {
  scraper_id: string;
  deployment_id: string;
  version: string;
  deployment_type: 'code_update' | 'config_update' | 'full_deployment';
  package_hash?: string;
  initiated_by: string;
  deployment_notes?: string;
}

export interface ScraperMetrics {
  scraper_id: string;
  timestamp: Date;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  companies_scraped: number;
  avg_response_time_ms: number;
  avg_memory_usage_mb: number;
  avg_cpu_usage_percent: number;
}

export interface ErrorPattern {
  pattern_id: string;
  scraper_id: string;
  error_type: string;
  frequency: number;
  first_occurred: Date;
  last_occurred: Date;
  suggested_fix?: string;
}

// ============================================================================
// SCRAPER LOGGING SERVICE CLASS
// ============================================================================

export class ScraperLoggingService {
  private static instance: ScraperLoggingService;
  private batchQueue: ScraperLogEvent[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 50;
  private readonly BATCH_TIMEOUT_MS = 5000;

  public static getInstance(): ScraperLoggingService {
    if (!ScraperLoggingService.instance) {
      ScraperLoggingService.instance = new ScraperLoggingService();
    }
    return ScraperLoggingService.instance;
  }

  // ============================================================================
  // CORE LOGGING METHODS
  // ============================================================================

  /**
   * Log a scraper event with metadata
   */
  public async logScraperEvent(
    scraperId: string,
    eventCode: number,
    details: Partial<ScraperLogEvent> = {}
  ): Promise<void> {
    try {
      const logEvent: ScraperLogEvent = {
        scraper_id: scraperId,
        event_code: eventCode,
        correlation_id: details.correlation_id || this.generateCorrelationId(),
        ...details
      };

      // Add to batch queue for performance
      this.addToBatch(logEvent);

      // Log critical events immediately
      if (this.isCriticalEvent(eventCode)) {
        await this.flushBatch();
      }

    } catch (error) {
      console.error('Failed to log scraper event:', error);
      // Fallback to console logging for critical events
      if (this.isCriticalEvent(eventCode)) {
        console.error(`CRITICAL SCRAPER EVENT [${scraperId}]:`, { eventCode, details });
      }
    }
  }

  /**
   * Log scraper operation events
   */
  public async logScraperOperation(
    scraperId: string,
    operation: 'start' | 'stop' | 'crash' | 'timeout',
    metadata: Record<string, any> = {}
  ): Promise<void> {
    const eventCodeMap = {
      start: EventCode.SCRAPER_STARTED,
      stop: EventCode.SCRAPER_STOPPED,
      crash: EventCode.SCRAPER_CRASHED,
      timeout: EventCode.SCRAPER_TIMEOUT
    };

    await this.logScraperEvent(scraperId, eventCodeMap[operation], {
      message: `Scraper ${operation} operation`,
      metadata: {
        operation,
        ...metadata
      }
    });
  }

  /**
   * Log parsing events with detailed results
   */
  public async logParsingEvent(
    scraperId: string,
    result: 'success' | 'failed' | 'partial' | 'suspicious',
    details: {
      source_url?: string;
      companies_found?: number;
      parsing_time_ms?: number;
      error_details?: string;
      raw_content?: string;
    } = {}
  ): Promise<void> {
    const eventCodeMap = {
      success: EventCode.PARSING_SUCCESS,
      failed: EventCode.PARSING_FAILED,
      partial: EventCode.PARSING_PARTIAL,
      suspicious: EventCode.PARSING_SUSPICIOUS_CONTENT
    };

    await this.logScraperEvent(scraperId, eventCodeMap[result], {
      message: `Parsing ${result}`,
      source_url: details.source_url,
      metadata: {
        companies_found: details.companies_found,
        parsing_time_ms: details.parsing_time_ms,
        error_details: details.error_details
      },
      raw_content: result === 'suspicious' ? details.raw_content : undefined
    });

    // Auto-queue suspicious content for AI analysis
    if (result === 'suspicious' && details.raw_content) {
      await this.queueForAIAnalysis({
        scraper_id: scraperId,
        raw_content: details.raw_content,
        content_type: 'html',
        source_url: details.source_url,
        context_metadata: {
          reason: 'parsing_failed',
          companies_expected: details.companies_found || 0
        },
        priority: 2
      });
    }
  }

  /**
   * Log network request events
   */
  public async logNetworkEvent(
    scraperId: string,
    result: 'success' | 'failed' | 'timeout' | 'blocked' | 'rate_limited',
    details: {
      url?: string;
      status_code?: number;
      response_time_ms?: number;
      error_message?: string;
      retry_count?: number;
    } = {}
  ): Promise<void> {
    const eventCodeMap = {
      success: EventCode.REQUEST_SUCCESS,
      failed: EventCode.REQUEST_FAILED,
      timeout: EventCode.REQUEST_TIMEOUT,
      blocked: EventCode.REQUEST_BLOCKED,
      rate_limited: EventCode.RATE_LIMIT_HIT
    };

    await this.logScraperEvent(scraperId, eventCodeMap[result], {
      message: `Network request ${result}`,
      source_url: details.url,
      metadata: {
        status_code: details.status_code,
        response_time_ms: details.response_time_ms,
        error_message: details.error_message,
        retry_count: details.retry_count
      }
    });
  }

  // ============================================================================
  // AI ANALYSIS METHODS
  // ============================================================================

  /**
   * Queue content for AI analysis
   */
  public async queueForAIAnalysis(request: AIAnalysisRequest): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('ai_analysis_queue')
        .insert({
          scraper_id: request.scraper_id,
          raw_content: request.raw_content,
          content_type: request.content_type,
          source_url: request.source_url,
          context_metadata: request.context_metadata || {},
          priority: request.priority || 3,
          status: 'pending'
        })
        .select('id')
        .single();

      if (error) throw error;

      // Log the queueing event
      await this.logScraperEvent(request.scraper_id, EventCode.AI_ANALYSIS_QUEUED, {
        message: 'Content queued for AI analysis',
        metadata: {
          analysis_id: data.id,
          content_type: request.content_type,
          priority: request.priority
        }
      });

      return data.id;
    } catch (error) {
      console.error('Failed to queue content for AI analysis:', error);
      throw error;
    }
  }

  /**
   * Process AI analysis results
   */
  public async recordAIAnalysisResult(
    analysisId: string,
    result: {
      patterns_detected?: any[];
      confidence_score?: number;
      analysis_result?: Record<string, any>;
      success: boolean;
      error_message?: string;
    }
  ): Promise<void> {
    try {
      const { data: queueItem, error: fetchError } = await supabase
        .from('ai_analysis_queue')
        .select('scraper_id')
        .eq('id', analysisId)
        .single();

      if (fetchError) throw fetchError;

      const { error: updateError } = await supabase
        .from('ai_analysis_queue')
        .update({
          status: result.success ? 'completed' : 'failed',
          analysis_result: result.analysis_result,
          confidence_score: result.confidence_score,
          patterns_detected: result.patterns_detected,
          processed_at: new Date().toISOString()
        })
        .eq('id', analysisId);

      if (updateError) throw updateError;

      // Log the completion event
      await this.logScraperEvent(queueItem.scraper_id, 
        result.success ? EventCode.AI_ANALYSIS_COMPLETED : EventCode.AI_ANALYSIS_FAILED, {
        message: result.success ? 'AI analysis completed' : 'AI analysis failed',
        metadata: {
          analysis_id: analysisId,
          confidence_score: result.confidence_score,
          patterns_found: result.patterns_detected?.length || 0,
          error_message: result.error_message
        }
      });

    } catch (error) {
      console.error('Failed to record AI analysis result:', error);
      throw error;
    }
  }

  // ============================================================================
  // DEPLOYMENT LOGGING METHODS
  // ============================================================================

  /**
   * Log deployment events
   */
  public async logDeployment(deployment: DeploymentEvent): Promise<void> {
    try {
      // Insert deployment record
      const { error: deploymentError } = await supabase
        .from('scraper_deployments')
        .insert({
          deployment_id: deployment.deployment_id,
          scraper_id: deployment.scraper_id,
          version: deployment.version,
          deployment_type: deployment.deployment_type,
          package_hash: deployment.package_hash,
          initiated_by: deployment.initiated_by,
          deployment_notes: deployment.deployment_notes,
          status: 'pending'
        });

      if (deploymentError) throw deploymentError;

      // Log deployment started event
      await this.logScraperEvent(deployment.scraper_id, EventCode.DEPLOYMENT_STARTED, {
        message: `Deployment ${deployment.deployment_id} started`,
        deployment_id: deployment.deployment_id,
        metadata: {
          version: deployment.version,
          deployment_type: deployment.deployment_type,
          initiated_by: deployment.initiated_by
        }
      });

    } catch (error) {
      console.error('Failed to log deployment:', error);
      throw error;
    }
  }

  /**
   * Update deployment status
   */
  public async updateDeploymentStatus(
    deploymentId: string,
    status: 'deploying' | 'success' | 'failed' | 'rolled_back',
    metadata: Record<string, any> = {}
  ): Promise<void> {
    try {
      const { data: deployment, error: fetchError } = await supabase
        .from('scraper_deployments')
        .select('scraper_id')
        .eq('deployment_id', deploymentId)
        .single();

      if (fetchError) throw fetchError;

      const updateData: any = { status };
      if (status === 'success' || status === 'failed') {
        updateData.completed_at = new Date().toISOString();
      }
      if (status === 'deploying') {
        updateData.started_at = new Date().toISOString();
      }

      const { error: updateError } = await supabase
        .from('scraper_deployments')
        .update(updateData)
        .eq('deployment_id', deploymentId);

      if (updateError) throw updateError;

      // Log status change
      const eventCode = status === 'success' ? EventCode.DEPLOYMENT_SUCCESS : 
                      status === 'failed' ? EventCode.DEPLOYMENT_FAILED : 
                      EventCode.DEPLOYMENT_STARTED;

      await this.logScraperEvent(deployment.scraper_id, eventCode, {
        message: `Deployment ${status}`,
        deployment_id: deploymentId,
        metadata
      });

    } catch (error) {
      console.error('Failed to update deployment status:', error);
      throw error;
    }
  }

  // ============================================================================
  // PERFORMANCE MONITORING
  // ============================================================================

  /**
   * Record performance metrics
   */
  public async recordPerformanceMetrics(metrics: ScraperMetrics): Promise<void> {
    try {
      const hourTimestamp = new Date(metrics.timestamp);
      hourTimestamp.setMinutes(0, 0, 0); // Truncate to hour

      const errorRate = metrics.total_requests > 0 ? 
        (metrics.failed_requests / metrics.total_requests) * 100 : 0;

      const { error } = await supabase
        .from('scraper_performance_hourly')
        .upsert({
          scraper_id: metrics.scraper_id,
          hour_timestamp: hourTimestamp.toISOString(),
          total_requests: metrics.total_requests,
          successful_requests: metrics.successful_requests,
          failed_requests: metrics.failed_requests,
          companies_scraped: metrics.companies_scraped,
          avg_response_time_ms: metrics.avg_response_time_ms,
          avg_memory_usage_mb: metrics.avg_memory_usage_mb,
          avg_cpu_usage_percent: metrics.avg_cpu_usage_percent,
          error_rate: errorRate
        }, {
          onConflict: 'scraper_id,hour_timestamp'
        });

      if (error) throw error;

      // Log performance milestone if significant
      if (errorRate > 10) {
        await this.logScraperEvent(metrics.scraper_id, EventCode.SUSPICIOUS_ACTIVITY, {
          message: 'High error rate detected',
          metadata: {
            error_rate: errorRate,
            failed_requests: metrics.failed_requests,
            total_requests: metrics.total_requests
          }
        });
      }

    } catch (error) {
      console.error('Failed to record performance metrics:', error);
      throw error;
    }
  }

  // ============================================================================
  // ERROR PATTERN ANALYSIS
  // ============================================================================

  /**
   * Detect error patterns for a scraper
   */
  public async detectErrorPatterns(scraperId: string, timeWindowHours: number = 24): Promise<ErrorPattern[]> {
    try {
      const { data, error } = await supabase.rpc('detect_scraper_error_patterns', {
        p_scraper_id: scraperId,
        p_time_window_hours: timeWindowHours
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to detect error patterns:', error);
      return [];
    }
  }

  // ============================================================================
  // BATCH PROCESSING
  // ============================================================================

  private addToBatch(event: ScraperLogEvent): void {
    this.batchQueue.push(event);

    if (this.batchQueue.length >= this.BATCH_SIZE) {
      this.flushBatch();
    } else if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => {
        this.flushBatch();
      }, this.BATCH_TIMEOUT_MS);
    }
  }

  private async flushBatch(): Promise<void> {
    if (this.batchQueue.length === 0) return;

    const events = [...this.batchQueue];
    this.batchQueue = [];
    
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }

    try {
      const { error } = await supabase
        .from('scraper_logs')
        .insert(events);

      if (error) {
        console.error('Failed to batch insert scraper logs:', error);
        // Re-queue events for retry
        this.batchQueue.unshift(...events);
      }
    } catch (error) {
      console.error('Batch flush error:', error);
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private isCriticalEvent(eventCode: number): boolean {
    const criticalEvents: number[] = [
      EventCode.SCRAPER_CRASHED,
      EventCode.DEPLOYMENT_FAILED,
      EventCode.UNAUTHORIZED_ACCESS,
      EventCode.SCRAPER_AUTH_FAILED
    ];
    return criticalEvents.includes(eventCode);
  }

  private generateCorrelationId(): string {
    return `scraper-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Graceful shutdown - flush remaining logs
   */
  public async shutdown(): Promise<void> {
    await this.flushBatch();
  }
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

export const scraperLogger = ScraperLoggingService.getInstance();

// Convenience methods for common operations
export const logScraperStart = (scraperId: string, metadata?: Record<string, any>) =>
  scraperLogger.logScraperOperation(scraperId, 'start', metadata);

export const logScraperStop = (scraperId: string, metadata?: Record<string, any>) =>
  scraperLogger.logScraperOperation(scraperId, 'stop', metadata);

export const logParsingSuccess = (scraperId: string, companiesFound: number, url?: string) =>
  scraperLogger.logParsingEvent(scraperId, 'success', { 
    companies_found: companiesFound, 
    source_url: url 
  });

export const logParsingFailure = (scraperId: string, error: string, url?: string, rawContent?: string) =>
  scraperLogger.logParsingEvent(scraperId, rawContent ? 'suspicious' : 'failed', {
    error_details: error,
    source_url: url,
    raw_content: rawContent
  });

export const queueForAI = (scraperId: string, content: string, url?: string, priority: 1 | 2 | 3 | 4 | 5 = 3) =>
  scraperLogger.queueForAIAnalysis({
    scraper_id: scraperId,
    raw_content: content,
    content_type: 'html',
    source_url: url,
    priority
  });